import { create } from 'zustand'
import rawProfile from '@/config/deployment/generated/active-profile.json'
import { parseDeploymentProfile } from '@/config/deployment/deployment.schema'
import type {
  CapabilityPolicy,
  CapabilityUnavailableBehavior,
  DeploymentCapability,
  DeploymentProfile
} from '@/config/deployment/deployment.types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function getDeploymentValue(
  profile: DeploymentProfile,
  capability: DeploymentCapability
): unknown {
  return capability.split('.').reduce<unknown>((current, segment) => {
    if (!isRecord(current)) return undefined
    return current[segment]
  }, profile)
}

export type ResolvedCapabilityPolicy = Required<
  Pick<CapabilityPolicy, 'enabled' | 'unavailableBehavior'>
> &
  Pick<CapabilityPolicy, 'message' | 'redirectTo'>

const defaultUnavailableBehavior: CapabilityUnavailableBehavior = 'hidden'

export function getCapabilityPolicy(value: unknown): ResolvedCapabilityPolicy {
  if (typeof value === 'boolean') {
    return { enabled: value, unavailableBehavior: defaultUnavailableBehavior }
  }

  if (isRecord(value) && typeof value.enabled === 'boolean') {
    return {
      enabled: value.enabled,
      unavailableBehavior:
        typeof value.unavailableBehavior === 'string'
          ? (value.unavailableBehavior as CapabilityUnavailableBehavior)
          : defaultUnavailableBehavior,
      ...(typeof value.message === 'string' ? { message: value.message } : {}),
      ...(typeof value.redirectTo === 'string' ? { redirectTo: value.redirectTo } : {})
    }
  }

  // 兼容父级对象（如 agent.templates）通过其 visible 字段作为可见性能力。
  if (isRecord(value) && typeof value.visible === 'boolean') {
    return { enabled: value.visible, unavailableBehavior: defaultUnavailableBehavior }
  }

  return { enabled: false, unavailableBehavior: defaultUnavailableBehavior }
}

export function isCapabilityEnabled(value: unknown): boolean {
  return getCapabilityPolicy(value).enabled
}

const initialProfile = parseDeploymentProfile(rawProfile)

interface DeploymentState {
  profile: DeploymentProfile
  getCapabilityPolicy: (capability: DeploymentCapability) => ResolvedCapabilityPolicy
  hasCapability: (capability: DeploymentCapability) => boolean
  setProfile: (profile: DeploymentProfile) => void
}

export const useDeploymentStore = create<DeploymentState>((set, get) => ({
  profile: initialProfile,
  getCapabilityPolicy: capability =>
    getCapabilityPolicy(getDeploymentValue(get().profile, capability)),
  hasCapability: capability =>
    getCapabilityPolicy(getDeploymentValue(get().profile, capability)).enabled,
  setProfile: profile => set({ profile: parseDeploymentProfile(profile) })
}))
